import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import type { AlternativeProductDTO } from "~/features/cart/cart/api/fetchAlternativeProducts";
import { fetchAlternativeProducts } from "~/features/cart/cart/api/fetchAlternativeProducts";
import { fetchProductInfo } from "~/domains/product/api/fetchProductInfo";
import { addBaseProductDataAction } from "~/domains/product/redux/actions";
import type { ProductData } from "~/domains/product/types/ProductData";

interface UseAlternateProductsDataInput {
  alternateProductsId: string;
}

export function useAlternateProductsData({ alternateProductsId }: UseAlternateProductsDataInput) {
  const [isLoading, setLoading] = useState(true);
  const [alternateProducts, setAlternateProducts] = useState<AlternativeProductDTO[]>([]);
  const dispatch = useDispatch();

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setAlternateProducts([]);

    const fetchData = async () => {
      const data: { alternativeProducts: AlternativeProductDTO[] } = await fetchAlternativeProducts(
        alternateProductsId,
      );

      if (cancelled) {
        return;
      }

      setAlternateProducts(data.alternativeProducts);

      const codes = data.alternativeProducts.map((product) => product.code);
      const results = await Promise.all(codes.map((code) => fetchProductInfo(code, true).catch(() => null)));
      const products = results.filter((product): product is ProductData => product !== null);

      if (cancelled) {
        return;
      }

      if (products.length > 0) {
        products.forEach((product) => {
          dispatch(addBaseProductDataAction(product));
        });
      }
      setLoading(false);
    };

    fetchData().catch(() => {
      if (!cancelled) {
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [alternateProductsId]);

  return { isLoading, alternateProducts };
}
